import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Brain, Loader2, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import ProgramReviewTable from './ProgramReviewTable';

// Admin tool: AI reads the lender's Notes/Guidelines -> proposed programs -> review -> approve into Product Matrix. Notes are never changed.
export const NotesProgramsPanel = ({ lender, isAdmin, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const ext = lender.notes_extraction;
  const hasText = ((lender.notes || '') + (lender.product_guidelines || '')).trim().length >= 60;

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/lenders/${lender.lender_id}/notes-programs/extract`);
      toast[r.data.programs.length ? 'success' : 'warning'](r.data.programs.length ? `${r.data.programs.length} program(s) proposed from Notes — review & approve below` : 'No loan programs could be identified in the Notes');
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Extraction failed'); } finally { setBusy(false); }
  };
  const dismiss = async () => {
    try { await api.post(`/lenders/${lender.lender_id}/notes-programs/dismiss`); toast.success('Extraction dismissed'); onChanged?.(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="border rounded-lg p-4 space-y-2 max-w-full overflow-hidden" data-testid="notes-programs-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="font-semibold flex items-center gap-2"><Brain className="h-4 w-4" /> Programs from Notes (AI)
          {ext?.status === 'pending_review' && <Badge className="bg-amber-100 text-amber-800 border-amber-300" variant="outline" data-testid="notes-ext-status">Pending admin review · {ext.programs.length}</Badge>}
          {ext?.status === 'approved' && <Badge className="bg-green-100 text-green-800 border-green-300" variant="outline" data-testid="notes-ext-status"><CheckCircle className="h-3 w-3 mr-1" />{ext.approved_program_ids?.length || 0} approved {ext.approved_at ? new Date(ext.approved_at).toLocaleDateString() : ''}</Badge>}
          {ext?.status === 'dismissed' && <Badge variant="outline" data-testid="notes-ext-status">Dismissed</Badge>}
          {ext?.status === 'nothing_found' && <Badge variant="outline" data-testid="notes-ext-status">Nothing found</Badge>}
          {ext?.status === 'error' && <Badge variant="outline" className="border-red-300 text-red-700" data-testid="notes-ext-status">Error</Badge>}
        </h4>
        {isAdmin && (
          <div className="flex gap-2">
            {ext?.status === 'pending_review' && <Button size="sm" variant="ghost" onClick={dismiss} data-testid="notes-ext-dismiss-btn"><XCircle className="h-4 w-4 mr-1" /> Dismiss</Button>}
            <Button size="sm" variant="outline" onClick={run} disabled={busy || !hasText} data-testid="notes-ext-run-btn">
              {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Reading Notes…</> : <><Brain className="h-4 w-4 mr-1" /> {ext ? 'Re-extract from Notes' : 'Extract programs from Notes'}</>}
            </Button>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground">Reads the Notes and Product Guidelines above, proposes one row per loan program, and flags anything unclear. The original Notes are never modified; nothing is used for matching until an admin approves it.</p>
      {!hasText && <p className="text-xs text-amber-700">No guideline text in Notes yet.</p>}
      {ext?.flags?.length > 0 && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="notes-ext-flags">
          <AlertTriangle className="inline h-3 w-3 mr-1" />Needs confirmation: <ul className="list-disc ml-5">{ext.flags.map((f, i) => <li key={i}>{f}</li>)}</ul>
        </div>
      )}
      {ext?.status === 'pending_review' && isAdmin && (
        <ProgramReviewTable key={ext.extraction_id} lenderId={lender.lender_id} doc={ext} idSuffix={ext.extraction_id}
          approvePath={`/lenders/${lender.lender_id}/notes-programs/approve`} approveBody={{ extraction_id: ext.extraction_id }} onApproved={onChanged} />
      )}
      {ext?.status === 'pending_review' && !isAdmin && <p className="text-xs text-muted-foreground">{ext.programs.length} proposed program(s) are waiting for an administrator to review and approve.</p>}
    </div>
  );
};

export default NotesProgramsPanel;
